import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { NewsletterConsentScreen } from '../../../services';
import { Switch } from '../Switch';
import { isSubscribedState, useNewsletterConsents } from './useNewsletterConsents';

import classNames from 'classnames';
import classes from './NewsletterConsent.module.scss';

interface NewsletterConsentProps {
  screen: NewsletterConsentScreen;
  titleId?: string;
}

export const NewsletterConsent: FunctionComponent<NewsletterConsentProps> = ({
  screen,
  titleId,
}) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'newsletters' });

  const { consents, setSubscribed, setAllSubscribed } = useNewsletterConsents(screen);

  if (!consents) {
    return null;
  }

  const { lists, privacyPolicyUrl } = consents;

  const isOnboarding = screen === NewsletterConsentScreen.ONBOARDING;

  const TitleTag = isOnboarding ? 'h2' : 'h1';

  return (
    <div className={classes['newsletter-consent']}>
      <div>
        <TitleTag
          id={titleId}
          className={classNames(classes['newsletter-consent__title'], {
            [classes['newsletter-consent__title--onboarding']]: isOnboarding,
          })}
        >
          {t('settings.newsletters')}
        </TitleTag>
        <div className={classes['newsletter-consent__sub-title']}>{ctxT('sub-title')}</div>
      </div>

      <div
        className={classNames(classes['newsletter-consent__lists'], {
          [classes['newsletter-consent__lists--onboarding']]: isOnboarding,
        })}
      >
        {lists.length > 1 && (
          <Switch
            label={ctxT('all')}
            className={classes['newsletter-consent__all']}
            checked={lists.every(({ state }) => isSubscribedState(state))}
            onChange={setAllSubscribed}
          />
        )}

        {lists.map(({ listKey, displayName, description, consentText, state }) => (
          <div key={listKey} className={classes['newsletter-consent__item']}>
            <div className={classes['newsletter-consent__row']}>
              <div className={classes['newsletter-consent__text']}>
                <span className={classes['newsletter-consent__name']}>{displayName}</span>
                {description && (
                  <span className={classes['newsletter-consent__description']}>{description}</span>
                )}
              </div>
              <Switch
                ariaLabel={displayName}
                checked={isSubscribedState(state)}
                onChange={(isSubscribed) => setSubscribed({ listKey, isSubscribed })}
              />
            </div>
            {consentText && (
              <div className={classes['newsletter-consent__consent']}>{consentText}</div>
            )}
          </div>
        ))}

        {privacyPolicyUrl && (
          <a
            className={classes['newsletter-consent__privacy-link']}
            href={privacyPolicyUrl}
            target={'_blank'}
            rel={'noreferrer'}
          >
            {ctxT('privacy-policy')}
          </a>
        )}
      </div>
    </div>
  );
};
